import { useFormField } from "@/components/elements/FormField";
import { background, button, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { ToggleButton, ToggleButtonGroup, ToggleButtonGroupProps, styled } from "@mui/material";
import { FC, ReactNode } from "react";

export type TToggleButtonGroupProps = {
  nonControl?: boolean;
  options: { value: string | number; label: ReactNode }[];
} & ToggleButtonGroupProps;

export const ToggleGroup: FC<TToggleButtonGroupProps> = ({ nonControl = false, options, ...rest }) => {
  const { field } = useFormField(nonControl);

  const handleChange = (e: React.MouseEvent<HTMLElement>, value: string | number) => {
    if (field) {
      field.onChange(value);
    }
    rest.onChange && rest.onChange(e, value);
  };

  return (
    <StyledToggleButtonGroup exclusive value={field?.value} {...rest} {...field} onChange={handleChange}>
      {options.map((option) => (
        <StyledToggleButton
          key={option.value}
          isNull={field?.value == null}
          value={option.value}
          sx={{ pointerEvents: field?.value === option.value ? "none" : "all" }}
        >
          {option.label}
        </StyledToggleButton>
      ))}
    </StyledToggleButtonGroup>
  );
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  display: "flex",
}));

const StyledToggleButton = styled(ToggleButton, {
  shouldForwardProp: (prop) => prop !== "isNull",
})<{ isNull?: boolean }>(({ theme, isNull }) => ({
  flex: 1,
  padding: 4,
  borderRadius: `${rounded.sm}`,
  border: isNull ? `1px solid ${background.disable} !important` : `1px solid ${theme.palette.divider}`,
  color: isNull ? text.tertiary : theme.palette.text.primary,
  backgroundColor: isNull ? background.disable : "transparent",
  pointerEvents: isNull ? "none" : "auto",
  "&.Mui-selected": {
    backgroundColor: button.primary,
    color: "#fff",
    borderColor: button.primary,
    "&:hover": {
      backgroundColor: button.primary,
      opacity: 0.9,
    },
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));
